import React, { useState } from "react";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { Swiper, SwiperSlide } from "swiper/react";
import SwiperCore, { Autoplay } from "swiper";
import "swiper/swiper-bundle.min.css";
import "react-lazy-load-image-component/src/effects/blur.css";
import { PropertyCard } from "properties/PropertyCard";
import { TPropertyListingMdl } from "properties/_models/PropertyMdl";
import styles from "./_css/featuredProperties.module.css";

SwiperCore.use([Autoplay]);

// Duree d'une carte pour le defilement automatique. Combinee a delay: 0 et a une
// transition lineaire (cf. .swiper-wrapper dans le module CSS), elle donne un glissement
// continu plutot qu'un saut d'une carte a l'autre.
const AUTOPLAY_SPEED = 14000;
// Les fleches, elles, doivent reagir tout de suite : slideNext/slidePrev acceptent
// une vitesse ponctuelle qui court-circuite AUTOPLAY_SPEED.
const ARROW_SPEED = 600;

export function FeaturedPropertiesCarousel({
    properties,
    pageBuilder,
}: {
    properties: TPropertyListingMdl[];
    pageBuilder?: boolean;
}) {
    const { t } = useTranslation();
    const [swiper, setSwiper] = useState<SwiperCore | null>(null);

    // On passe par slideNext/slidePrev pour que le carrousel se recale toujours sur une
    // carte : deplacer la translation a la main le laissait entre deux cartes, et le
    // loopFix de l'autoplay le repositionnait alors d'un coup sec.
    // L'autoplay est stoppe le temps du deplacement, sinon il reprend la main aussitot.
    const slideBy = (direction: 1 | -1) => {
        if (!swiper) return;
        swiper.autoplay.stop();
        if (direction === 1) swiper.slideNext(ARROW_SPEED);
        else swiper.slidePrev(ARROW_SPEED);
        swiper.once("transitionEnd", () => swiper.autoplay.start());
    };

    return (
        <div className={styles.carousel}>
            {/* fleches au-dessus des cartes, comme le carrousel "Similar properties" de la fiche */}
            <div className={styles.navRow}>
                <button
                    type="button"
                    aria-label={t("homePage.featuredCarousel.previous")}
                    className={clsx(styles.navButton, styles.navButtonPrev)}
                    onClick={() => slideBy(-1)}
                />
                <button
                    type="button"
                    aria-label={t("homePage.featuredCarousel.next")}
                    className={clsx(styles.navButton, styles.navButtonNext)}
                    onClick={() => slideBy(1)}
                />
            </div>
            <Swiper
                id="featuredProperties"
                onSwiper={setSwiper}
                slidesPerView={3}
                spaceBetween={30}
                speed={AUTOPLAY_SPEED}
                autoplay={{ delay: 0, disableOnInteraction: false, pauseOnMouseEnter: true }}
                loop
                // par defaut Swiper ignore slideNext/slidePrev tant qu'une transition tourne.
                // L'autoplay en enchainant sans interruption, les fleches ne repondaient jamais.
                loopPreventsSlide={false}
                // surtout ne pas activer les options d'observation du DOM ici : elles appellent
                // update() a chaque mutation, et update() hors freeMode fait un
                // slideTo(activeIndex, 0) qui saute d'un coup sur la carte visee. Les cartes
                // chargent leurs images en lazy, donc au chargement de la page ca enchainait
                // les bonds. Swiper React se met deja a jour quand ses enfants changent.
                breakpoints={{
                    0: { slidesPerView: 1.1, spaceBetween: 16 },
                    600: { slidesPerView: 1.6, spaceBetween: 20 },
                    750: { slidesPerView: 2, spaceBetween: 24 },
                    1100: { slidesPerView: 3, spaceBetween: 30 },
                }}
            >
                {properties.map((property) => {
                    if (!property) return null;
                    return (
                        <SwiperSlide key={property._id}>
                            <PropertyCard isFeaturedCard pageBuilder={pageBuilder} property={property} />
                        </SwiperSlide>
                    );
                })}
            </Swiper>
        </div>
    );
}
